import {
	digitSafeSegment,
	labelSegment,
} from '@dealstack/shared/lib/common/business/documentTemplate';
import {
	SCOPED_SOURCES,
	SECURITY_SCOPED_DATA_POINTS,
	type ScopedSource,
} from '@dealstack/shared/lib/documents/fieldBindingCatalogue';

// How a security-scoped or scheme-scoped Word field is named and labelled. Shared because the
// palette mints the names, the chips draw the labels, and the server-side field-placement PDF
// needs both.

/** Anything that identifies a security well enough to name a field after it. */
export interface NameableSecurity {
	id: string;
	name: string;
}

/** A security together with the entity issuing it, which is what a label shows. */
export interface LabelledSecurity extends NameableSecurity {
	issuerId: string;
	issuerName: string;
}

/**
 * Whether security labels must name their issuer: only once the securities span more than one
 * entity, where two of them can each issue an "Ordinary Shares". A single-entity programme's
 * labels stay exactly as they were.
 */
export const spansIssuers = (securities: readonly LabelledSecurity[]): boolean =>
	new Set(securities.map((security) => security.issuerId)).size > 1;

/** A security's name as a label shows it, followed by its issuer when `showIssuer` and it has one. */
export const securityDisplayName = (security: LabelledSecurity, showIssuer: boolean): string =>
	showIssuer && security.issuerName !== ''
		? `${security.name} (${security.issuerName})`
		: security.name;

/** Anything that identifies a cohort scheme (a Tag) well enough to name a field after it. */
export interface NameableScheme {
	id: string;
	name: string;
}

// DOUBLE underscore: `labelSegment` collapses non-alphanumeric runs to a single `_`, so neither
// a slug nor a sanitised id can contain `__` and the split stays unambiguous.
const ID_DELIMITER = '__';

// Singular, because a scoped path is relative to ONE holding or ONE allocation.
const SOURCE_SEGMENT: Record<ScopedSource, string> = {
	holdings: 'holding',
	allocations: 'allocation',
};

/**
 * The whole id, not a prefix of it. Ids are UUIDv7, so any prefix is a timestamp bucket rather
 * than an identifier — on production data an 8-character one put 64% of securities in a
 * colliding group, and even a full-millisecond one collides for anything bulk-created.
 */
const idSegment = (id: string): string => labelSegment(id);

/**
 * The segment identifying one security inside a scoped field name.
 *
 * Ends in the id because the name alone collides: uniqueness is only
 * `(issuerId, name, type)` with no DB constraint, `labelSegment` collapses punctuation, and a
 * non-Latin name sanitises to ''. One name for two securities repointed an already-placed field.
 * The id comes from the security alone — a set-dependent one would rename existing fields
 * the day a similar security was created.
 */
export const securitySegment = (security: NameableSecurity): string => {
	const slug = digitSafeSegment(labelSegment(security.name));
	const idPart = idSegment(security.id);
	// `security` rather than an empty leading segment, which would make the whole name
	// ungrammatical and render the field blank while still reporting it bound.
	return slug === '' ? `security${ID_DELIMITER}${idPart}` : `${slug}${ID_DELIMITER}${idPart}`;
};

/** The same treatment for a cohort scheme, whose name is likewise author-written and mutable. */
export const schemeSegment = (scheme: NameableScheme): string => {
	const slug = digitSafeSegment(labelSegment(scheme.name));
	const idPart = idSegment(scheme.id);
	return slug === '' ? `scheme${ID_DELIMITER}${idPart}` : `${slug}${ID_DELIMITER}${idPart}`;
};

/**
 * The Word field name a scoped data point is offered under.
 *
 * All three segments are load-bearing. The SOURCE especially: every `source: 'either'` point is
 * offered in both radio positions reading a different list, so one name would cover two
 * expressions and the save (which merges by name, incoming wins) would repoint a placed field.
 */
export const scopedFieldName = (
	security: NameableSecurity,
	source: ScopedSource,
	label: string
): string =>
	[securitySegment(security), SOURCE_SEGMENT[source], digitSafeSegment(labelSegment(label))].join(
		'.'
	);

/** The Word field name a scheme's cohort data point is offered under. */
export const schemeFieldName = (scheme: NameableScheme): string =>
	`investor.schemes.${schemeSegment(scheme)}`;

/**
 * The label a scoped field shows in the palette, on the chip and in the placement PDF.
 *
 * One function for all three: a chip resolves its text through the palette, so a shorter label
 * there made the chip's text change on every reopen (the pin is not part of the document).
 */
export const scopedFieldLabel = ({
	securityName,
	source,
	label,
	inProgressLabel,
}: {
	securityName: string;
	source: ScopedSource;
	label: string;
	inProgressLabel: string;
}): string =>
	source === 'allocations'
		? `${securityName} (${inProgressLabel}) · ${label}`
		: `${securityName} · ${label}`;

/** The label a scheme's cohort data point is shown under. */
export const schemeFieldLabel = (scheme: NameableScheme): string => `${scheme.name} (cohort)`;

// Every name the programme could produce. All securities and both sources, not just what is
// pinned: the pin is editor state and is gone on reopen, but the placed field is not.

export const knownFieldLabelsFor = ({
	securities,
	schemes = [],
	inProgressLabel,
}: {
	securities: readonly LabelledSecurity[];
	schemes?: readonly NameableScheme[];
	inProgressLabel: string;
}): Record<string, string> => {
	const labels: Record<string, string> = {};
	const showIssuer = spansIssuers(securities);
	for (const security of securities) {
		for (const source of SCOPED_SOURCES) {
			for (const point of SECURITY_SCOPED_DATA_POINTS) {
				if (point.source !== 'either' && point.source !== source) {
					continue;
				}
				labels[scopedFieldName(security, source, point.label)] = scopedFieldLabel({
					securityName: securityDisplayName(security, showIssuer),
					source,
					label: point.label,
					inProgressLabel,
				});
			}
		}
	}
	for (const scheme of schemes) {
		labels[schemeFieldName(scheme)] = schemeFieldLabel(scheme);
	}
	return labels;
};

/**
 * What makes a placed field the same field across a rename: the id, the source and the
 * label slug. The cosmetic slug is excluded precisely because a rename changes it.
 */
export type FieldIdentity = string;

const identityOf = (id: string, rest: string[]): FieldIdentity => [id, ...rest].join('/');

/** Splits `<slug>__<id>` into its two halves, or null when there is no id part. */
const splitSegment = (segment: string): { slug: string; id: string } | null => {
	const at = segment.lastIndexOf(ID_DELIMITER);
	if (at === -1) {
		return null;
	}
	return {
		slug: segment.slice(0, at),
		id: segment.slice(at + ID_DELIMITER.length),
	};
};

/** `<slug>__<id>.<holding|allocation>.<labelSlug>`, or null when the name is not one. */
export const scopedFieldIdentity = (name: string): FieldIdentity | null => {
	const segments = name.split('.');
	const [securitySeg, sourceSeg, labelSeg] = segments;
	// Elements checked as well as the length: under `noUncheckedIndexedAccess` the length check
	// does not narrow a destructured element.
	if (
		segments.length !== 3 ||
		securitySeg === undefined ||
		sourceSeg === undefined ||
		labelSeg === undefined ||
		labelSeg === ''
	) {
		return null;
	}
	const split = splitSegment(securitySeg);
	if (split === null || split.id === '') {
		return null;
	}
	if (sourceSeg !== SOURCE_SEGMENT.holdings && sourceSeg !== SOURCE_SEGMENT.allocations) {
		return null;
	}
	return identityOf(split.id, [sourceSeg, labelSeg]);
};

/** The identity of a scheme field name, or null when the name is not one. */
export const schemeFieldIdentity = (name: string): FieldIdentity | null => {
	const prefix = 'investor.schemes.';
	if (!name.startsWith(prefix)) {
		return null;
	}
	const split = splitSegment(name.slice(prefix.length));
	if (split === null || split.id === '') {
		return null;
	}
	return identityOf(split.id, ['scheme']);
};

/** Either identity, whichever the name turns out to be. */
export const fieldIdentity = (name: string): FieldIdentity | null =>
	scopedFieldIdentity(name) ?? schemeFieldIdentity(name);

/**
 * Resolves any field name to its CURRENT label: exact name first, then identity.
 *
 * The identity pass is what survives a rename — and it resolves to the new label, so the author
 * sees what the security or scheme is called now.
 */
export const fieldLabelResolverFor = ({
	securities,
	schemes = [],
	inProgressLabel,
}: {
	securities: readonly LabelledSecurity[];
	schemes?: readonly NameableScheme[];
	inProgressLabel: string;
}): ((name: string) => string | undefined) => {
	const byName = knownFieldLabelsFor({ securities, schemes, inProgressLabel });
	const byIdentity = new Map<FieldIdentity, string>();
	// Identity is the whole id, so distinct entities cannot share one. Kept as an invariant: if
	// two ever do, withhold the label rather than let the last writer win and label a field as
	// the wrong security.
	const ambiguous = new Set<FieldIdentity>();
	for (const [name, label] of Object.entries(byName)) {
		const identity = fieldIdentity(name);
		if (identity === null) {
			continue;
		}
		if (byIdentity.has(identity)) {
			ambiguous.add(identity);
			continue;
		}
		byIdentity.set(identity, label);
	}
	return (name: string): string | undefined => {
		const exact = byName[name];
		if (exact !== undefined) {
			return exact;
		}
		const identity = fieldIdentity(name);
		if (identity === null || ambiguous.has(identity)) {
			return undefined;
		}
		return byIdentity.get(identity);
	};
};
